import { ErrorHandler, Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { faro } from '@grafana/faro-web-sdk';
import { environment } from '@environments/environment';

/**
 * ErrorHandler global que reporta errores a Sentry y Grafana Faro.
 * Captura la ruta activa para asociar errores a vistas específicas.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private sentryHandler = Sentry.createErrorHandler({ showDialog: false });
  private isFaroAvailable?: boolean;
  private router = inject(Router);

  handleError(error: any): void {
    this.sentryHandler.handleError(error);
    this.reportToFaro(error);

    if (environment.showLogs) {
      console.error('❌ Error:', error);
    }
  }

  private reportToFaro(error: any): void {
    if (this.isFaroAvailable === undefined) {
      this.isFaroAvailable = !!(faro?.api?.pushError);
      if (!this.isFaroAvailable && environment.showLogs) {
        console.warn('⚠️ Grafana Faro no disponible');
      }
    }

    if (this.isFaroAvailable) {
      try {
        const errorObj = error instanceof Error ? error : new Error(String(error));

        // Capturar contexto de la ruta activa
        const currentRoute = this.router.url;

        // Agregar contexto adicional al error
        faro.api.setView({ name: currentRoute });
        faro.api.pushError(errorObj, {
          context: {
            route: currentRoute,
            component: this.extractComponentName(error)
          }
        });
      } catch (err) {
        if (environment.showLogs) {
          console.error('❌ Error reportando a Faro:', err);
        }
        this.isFaroAvailable = false;
      }
    }
  }

  private extractComponentName(error: any): string {
    // Intentar extraer el nombre del componente del stack trace
    if (error?.stack) {
      const match = error.stack.match(/at (\w+Component)/);
      return match ? match[1] : 'Unknown';
    }
    return 'Unknown';
  }
}